import React, { useEffect } from "react";
import { Navigate, Route, useNavigate } from "react-router-dom";
import { setAuthorization } from "../helpers/api_helper";
import { useDispatch, useSelector } from "react-redux";
import { useProfile } from "../Components/Hooks/UserHooks";
import { logoutUser } from "../slices/auth/login/thunk";
import { loginSuccess } from "../slices/auth/login/reducer";
import { getLanguage, getMenuData, getNotifications } from "../slices/auth/menu/thunk";
import { createSelector } from "reselect";
import { getCompanyUsers } from "../slices/auth/settings/thunk";
import { setPreloader } from "../slices/layouts/reducer";
import { getPermissionList, getPermissions } from "../slices/auth/permission/thunk";

/**
 * AuthProtected component for protecting routes based on user authentication.
 *
 * This component checks the user's authentication status and redirects to the
 * login page if the user is not authenticated. If authenticated, it dispatches
 * actions to store user profile and menu data in the Redux state.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {ReactNode} props.children - The content to be rendered if the user is authenticated.
 * @returns {ReactNode} The rendered AuthProtected component.
 */
const AuthProtected = (props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { userProfile, loading, token, userId } = useProfile();
	const selectPermission = createSelector(state => state.Permission, state => state.permission);
	const permission = useSelector(selectPermission);

	useEffect(() => {
		async function checkUserLoggedIn() {
			if (userProfile && token) {
				setAuthorization(token, userId);
				dispatch(setPreloader(true));
				await dispatch(getPermissions());
				const res = await dispatch(getPermissionList());
				if (res && res.hasOwnProperty('payload') && res?.payload.hasOwnProperty('status') && res?.payload.status === 'err') {
					navigate('/home');
				} else {
					dispatch(loginSuccess(userProfile));
					await dispatch(getMenuData());
					await dispatch(getCompanyUsers());
					await dispatch(getNotifications()); 
				}
				dispatch(setPreloader(false));
			}
		}
		checkUserLoggedIn();
	}, [token]);

	/*
	Navigate is un-auth access protected routes via url
	*/

	if (!userProfile && loading && !token) {
		return (
			<Navigate to={{ pathname: "/home", state: { from: props.location } }} />
		);
	}

	const selectLoginState = createSelector((state) => state, (state) => state.Login);
	const login = useSelector(selectLoginState);
	const selectMenuState = createSelector((state) => state.MenuData, (state) => state.client);
	const menuState = useSelector(selectMenuState);
	const selectUserList = createSelector((state) => state.Settings, (state) => state.userList);
	const userList = useSelector(selectUserList);


	if (login.user.status === "ok" && menuState !== null && userList !== null) {
		return <>{props.children}</>;
	}
};

/**
 * AccessRoute component for rendering a route with access to its children.
 *
 * This component is used to define routes with specific access control.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ComponentType} props.component - The component to be rendered for the route.
 * @param {rest} props.rest - Additional properties for the Route component.
 * @returns {ReactNode} The rendered AccessRoute component.
 */
const AccessRoute = ({ component: Component, ...rest }) => {
	return (
		<Route
			{...rest}
			render={props => {
				return (<> <Component {...props} /> </>);
			}}
		/>
	);
};

export { AuthProtected, AccessRoute };